<template>
  <v-container fluid class="stages-container pa-0">
    <!-- Modern Header -->
    <div
      v-if="showHeader"
      class="header-section mb-4"
      style="position: relative;"
    >
      <!-- Close Button -->
      <v-btn icon small class="header-close-btn" @click="dismissHeader">
        <v-icon color="white">mdi-close</v-icon>
      </v-btn>
      <div class="d-flex align-center mb-4">
        <div class="header-icon-wrapper mr-4">
          <v-icon size="32" color="#FFFFFF">mdi-view-column-outline</v-icon>
        </div>
        <div>
          <h1 class="page-title mb-1">Pipeline Stages</h1>
          <p class="page-subtitle mb-0">
            Manage your sales pipeline and move deals forward
          </p>
        </div>
        <v-spacer></v-spacer>
        <div class="d-none d-sm-flex">
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-drag</v-icon>
            Drag & Drop
          </v-chip>
          <v-chip
            class="mr-2 white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-eye-outline</v-icon>
            Visual
          </v-chip>
          <v-chip
            class="white--text"
            color="white"
            small
            style="background: rgba(255,255,255,0.25) !important; border: 1px solid rgba(255,255,255,0.5);"
          >
            <v-icon left x-small color="white">mdi-tune</v-icon>
            Customizable
          </v-chip>
        </div>
      </div>

      <!-- Feature Pills -->
      <v-row class="feature-pills ma-0">
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-robot-excited-outline</v-icon
            >
            <span>Automation</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-filter-variant</v-icon
            >
            <span>Smart Filters</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-currency-usd</v-icon
            >
            <span>Deal Value</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-account-multiple</v-icon
            >
            <span>Team View</span>
          </div>
        </v-col>
        <v-col cols="12" sm="6" md="4" lg="2" class="pa-1">
          <div class="feature-pill">
            <v-icon size="18" color="white" class="mr-2"
              >mdi-chart-timeline-variant</v-icon
            >
            <span>Track Progress</span>
          </div>
        </v-col>
      </v-row>
    </div>

    <!-- Filters Section -->
    <v-row class="ma-0 mb-4">
      <v-col cols="12" class="pa-2">
        <v-card class="modern-card customBorderRadius" elevation="0">
          <div class="card-header">
            <div class="d-flex align-center">
              <div class="icon-badge mr-3">
                <v-icon color="#FFFFFF">mdi-filter-outline</v-icon>
              </div>
              <div>
                <h3 class="card-title mb-0">Filters</h3>
                <p class="card-subtitle mb-0">
                  Filter and search your pipeline
                </p>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <v-card-text class="pa-4">
            <v-row class="ma-0">
              <v-col cols="12" md="4" class="pa-2">
                <v-autocomplete
                  v-model="selectedList"
                  :items="lists"
                  item-text="name"
                  item-value="id"
                  label="Select List"
                  prepend-inner-icon="mdi-format-list-bulleted"
                  autocomplete="false"
                  hide-details
                  dense
                  outlined
                  class="modern-input"
                  @change="refresh"
                >
                  <template v-slot:prepend-inner>
                    <v-icon color="primary">mdi-format-list-bulleted</v-icon>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="12" md="4" class="pa-2">
                <DispoComponent @filterByDispo="dispoFilter" />
              </v-col>
              <v-col cols="12" md="3" class="pa-2">
                <Search @searchCon="searchInAllStages" @reset="resetAll" />
              </v-col>
              <v-col cols="12" md="1" class="pa-2 d-flex align-center">
                <v-btn
                  color="primary"
                  depressed
                  large
                  block
                  @click="automationDialog = true"
                >
                  <v-icon>mdi-robot-excited-outline</v-icon>
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Stages Container -->
    <v-row v-if="stages.length > 0" class="ma-0">
      <v-col cols="12" class="px-3 py-2">
        <v-card
          class="modern-card stages-wrapper customBorderRadius"
          elevation="0"
        >
          <v-card-text class="pa-4 customBorderRadius">
            <div class="stages-scroll-container">
              <div
                v-for="(item, index) in stages"
                :key="index"
                class="stage-column"
                :style="
                  `width: ${item.pipelineview *
                    68}px; min-width: ${item.pipelineview * 68}px;`
                "
              >
                <!-- Minimized Stage -->
                <div v-if="item.pipelineview == 1" class="stage-minimized">
                  <div class="d-flex justify-center">
                    <v-btn
                      icon
                      small
                      color="primary"
                      @click="changeWidth(item)"
                    >
                      <v-icon>mdi-arrow-expand</v-icon>
                    </v-btn>
                  </div>
                  <div class="stage-minimized-content">
                    <h3 class="stage-minimized-title">
                      {{ item.name }}
                      <TotalDealValue
                        v-if="item.id"
                        :list="selectedList"
                        :stage-id="item.id ? item.id : ''"
                      />
                    </h3>
                  </div>
                </div>

                <!-- Expanded Stage -->
                <v-card v-else flat class="stage-card customBorderRadius">
                  <!-- Stage Header -->
                  <div class="stage-header">
                    <v-text-field
                      v-model="item.name"
                      solo
                      flat
                      dense
                      single-line
                      hide-details
                      class="stage-name-input"
                      background-color="transparent"
                      @blur="newStageName(item)"
                    />
                    <div class="stage-count-badge">
                      <v-chip small color="primary" text-color="white">
                        <v-icon x-small left>mdi-counter</v-icon>
                        {{ item.count || 0 }}
                      </v-chip>
                    </div>
                    <div class="stage-actions">
                      <v-btn icon x-small @click="beforesearch(index)">
                        <v-icon size="18">mdi-magnify</v-icon>
                      </v-btn>
                      <v-btn icon x-small @click="changeWidth(item)">
                        <v-icon size="18">mdi-arrow-collapse</v-icon>
                      </v-btn>
                      <v-btn
                        v-if="item.id"
                        icon
                        x-small
                        color="error"
                        @click="removeStage(item)"
                      >
                        <v-icon size="18">mdi-close</v-icon>
                      </v-btn>
                    </div>
                  </div>

                  <!-- Search Row -->
                  <div v-if="searchSelected == index" class="stage-search">
                    <v-text-field
                      v-model="search"
                      solo
                      flat
                      dense
                      single-line
                      hide-details
                      placeholder="Search in this stage..."
                      prepend-inner-icon="mdi-magnify"
                      class="search-input"
                    >
                      <template v-slot:append>
                        <v-icon @click="searchCon(item)"
                          >mdi-arrow-right-bold</v-icon
                        >
                        <v-icon @click="resetSearchCon(item)"
                          >mdi-close-circle</v-icon
                        >
                      </template>
                    </v-text-field>
                  </div>

                  <v-divider></v-divider>

                  <!-- Stage Content -->
                  <div
                    :class="[
                      'stage-content',
                      { 'stage-content-search': index == searchSelected },
                    ]"
                  >
                    <Stage
                      :ref="'stage' + item.id"
                      :list="selectedList"
                      :stage-id="item.id ? item.id : ''"
                      :dispo-new="disposSelected"
                      @refresh="refresh()"
                      @updateCount="handleStageCount"
                    />
                  </div>
                </v-card>
              </div>

              <!-- Add Stage Button -->
              <div class="add-stage-column">
                <v-card flat class="add-stage-card">
                  <v-btn
                    color="primary"
                    outlined
                    large
                    block
                    @click="addStage()"
                  >
                    <v-icon left>mdi-plus</v-icon>
                    Add Stage
                  </v-btn>
                </v-card>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- No Stages Message -->
    <v-row v-else class="ma-0">
      <v-col cols="12" class="px-3 py-2">
        <v-card class="modern-card" elevation="0">
          <v-card-text class="pa-8 text-center">
            <v-img
              class="mx-auto mb-4"
              width="250"
              src="../assets/assignMembers.png"
            />
            <h2 class="grey--text text--darken-1 mb-2">
              Select a List to View Stages
            </h2>
            <p class="grey--text">
              No stages available when "All lists" is selected. Please select a
              single list.
            </p>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Automation Dialog -->
    <v-dialog v-model="automationDialog" max-width="850">
      <v-card class="modern-card">
        <div class="card-header">
          <div class="d-flex align-center">
            <div class="icon-badge mr-3">
              <v-icon color="#FFFFFF">mdi-robot-excited-outline</v-icon>
            </div>
            <div>
              <h3 class="card-title mb-0">Automation</h3>
              <p class="card-subtitle mb-0">
                Configure automatic actions and workflows
              </p>
            </div>
          </div>
          <div class="d-flex align-center">
            <v-btn
              icon
              small
              color="primary"
              href="https://youtu.be/mJY9WJcSE9I"
              target="_blank"
              class="mr-2"
            >
              <v-icon>mdi-play-circle</v-icon>
            </v-btn>
            <v-btn icon @click="automationDialog = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
        </div>

        <v-divider></v-divider>

        <v-card-text class="pa-4">
          <automation-rules
            :list="
              lists.find((r) => {
                return r.id == selectedList;
              })
            "
            :all-lists="lists"
            @refresh="refresh()"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Vue from "vue";
import { mapGetters } from "vuex";

import fb from "../js/firebase.js";
import Stage from "../components/stage.vue";
import TotalDealValue from "../components/totalDealValue.vue";
import DispoComponent from "../components/dispoComponent.vue";
import automationRules from "../components/automationRules.vue";
import Search from "../components/search.vue";
export default {
  components: {
    Stage,
    TotalDealValue,
    DispoComponent,
    Search,
    automationRules,
  },
  data() {
    return {
      showHeader: false,
      lists: [],
      stages: [],
      disposSelected: [],
      search: "",
      searchSelected: -1,
      dueDateDialog: false,
      dueDate: "",
      selectedContact: "",
      loader: false,
      tempStage: {},
      filterActive: false,
      automationDialog: false,
    };
  },
  computed: {
    ...mapGetters([
      "ENDPOINT",
      "user",
      "dispoList",
      "fieldList",
      "teams",
      "selectedTeam",
    ]),
    nostages() {
      return require("../assets/noListContact.png");
    },
    selectedList: {
      get: function() {
        return this.$store.getters.selectedList;
      },
      set: function(newValue) {
        this.$store.commit("setSelectedList", newValue);
      },
    },
  },
  async created() {
    // Check if header was dismissed
    this.showHeader =
      localStorage.getItem("pipelineHeaderDismissed") !== "true";
    await this.fetchLists();
    await this.getStages();
  },
  methods: {
    dismissHeader() {
      this.showHeader = false;
      localStorage.setItem("pipelineHeaderDismissed", "true");
    },
    handleStageCount(data) {
      console.log("data", data);

      // If stageId is an empty string, update the first stage's count
      if (data.stageId === "") {
        if (this.stages.length > 0) {
          this.$set(this.stages[0], "count", data.count);
        }
      } else {
        // Otherwise, find the stage by ID and update its count
        let stage = this.stages.find((stage) => stage.id === data.stageId);
        if (stage) {
          this.$set(stage, "count", data.count);
        }
      }
    },
    async changeWidth(item) {
      var payload = {};
      if (item.pipelineview == 1) {
        payload.pipelineview = 4;
        item.pipelineview = 4;
      } else {
        payload.pipelineview = 1;
        item.pipelineview = 1;
      }
      if (item.id) {
        await this.$http.patch(`${this.ENDPOINT}/stages/${item.id}`, payload);
      }
      console.log("Changed width");
      console.log("this.search", this.search);
      if (item.pipelineview == 4 && this.search != "") {
        this.$refs[`stage${item.id}`][0].conSearch(this.search);
      }

      this.refresh();
    },
    async newStageName(item) {
      if (item.id) {
        var payload = {};
        payload.name = item.name;
        await this.$http.patch(`${this.ENDPOINT}/stages/${item.id}`, payload);
      }

      this.refresh();
    },
    async addStage() {
      var stage = {};
      stage.name = "New Stage";
      stage.desc = "Add stage discription";
      stage.pipelineview = 4;
      stage.listId = this.selectedList;
      var res = await this.$http
        .post(`${this.ENDPOINT}/stages/`, stage)
        .then((res) => {
          console.log("res.body: ", res.body);
          this.stages.push(res.body);
          console.log("stages: ", this.stages);

          this.$swal({ type: "success", text: "New Stage created" });
        })
        .catch((e) => {
          console.log("err", e);
        });
      try {
        fb.log("new_stage");
      } catch (e) {
        console.log("Error in FB log", e);
      }
    },
    async removeStage(stage) {
      // var index = this.stages.indexOf(stage);
      this.$swal({
        title: "Are you sure?",
        type: "warning",
        showCancelButton: true,
        confirmButtonColor: "#31a848",
        cancelButtonColor: "#f44336",
        confirmButtonText: "Yes, Remove it!",
      }).then((willDelete) => {
        if (willDelete.value) {
          this.$http
            .delete(`${this.ENDPOINT}/stages/${stage.id}`)
            .then((response) => {
              this.$swal({ type: "success", text: "Successful" });
              // this.stages.splice(index, 1);
              this.refresh();
              try {
                fb.log("remove_stage");
              } catch (e) {
                console.log("Error in log", e);
              }
            })
            .catch((err) => {
              console.log("err", err);
              this.$swal({ type: "error", text: err.body.error.message });
            });
        }
      });
    },
    getTeamIds() {
      var teams = [];
      if (this.selectedTeam == "allTeams") {
        teams = _.map(this.teams, "id");
      } else {
        teams.push(this.selectedTeam);
      }
      teams = _.without(teams, "allTeams");
      return teams;
    },
    async fetchLists() {
      try {
        this.lists = [];
        var response = [];
        var teamIds = this.getTeamIds();
        // Teams sections
        response = await this.$http.post(`${this.ENDPOINT}/lists/fetch`, {
          teams: teamIds,
        });
        this.lists = response.body;
        if (!this.selectedList) {
          this.selectedList = this.lists[0].id;
        }
        // console.log("List: ",this.lists)
      } catch (e) {
        console.log("error in list fetch", e);
      }
    },
    async getStages() {
      this.stages = [];
      var newStage = {
        name: "New",
        desc: "Contacts without Stage Id",
        pipelineview: 4,
        listId: this.selectedList,
      };
      var listId = this.selectedList;
      var response = await this.$http.get(
        `${this.ENDPOINT}/lists/${listId}/stages`,
      );
      this.stages = response.body;
      this.stages.splice(0, 0, newStage);
      this.stages.forEach((s) => {
        s["edit"] = false;
      });
      // console.log("stages: ",this.stages);
    },
    beforesearch(index) {
      this.searchSelected = index;
      this.search = "";
    },
    async searchCon(stage) {
      this.$refs[`stage${stage.id}`][0].conSearch(this.search);
    },
    resetSearchCon(stage) {
      this.searchSelected = -1;
      this.search = "";
      this.$refs[`stage${stage.id}`][0].removeSearch();
    },
    async refresh() {
      console.log("refreshed: ");
      await this.getStages();
      this.search = "";
      this.searchSelected = -1;
    },
    searchInAllStages(search) {
      if (search != "") {
        if (this.searchSelected > -1) {
          this.searchSelected = -1;
        }
        this.search = search;

        console.log("search is", this.search);
        for (var i in this.stages) {
          var stage = this.stages[i];
          console.log("stage is created", typeof stage.id);
          // if (typeof stage.id == "string") {
          //   console.log(
          //     "this.$refs",

          //   );

          console.log("this.$refs", this.$refs);

          this.$refs[`stage${stage.id}`][0].conSearch(search);
          // }
        }
      } else {
        this.resetAll();
      }
    },
    resetAll() {
      console.log("this.stages", this.stages);

      for (var i in this.stages) {
        var stage = this.stages[i];
        this.$refs[`stage${stage.id}`][0].removeSearch();
      }
    },
    dispoFilter(selectedDispo) {
      console.log("selectedDispo", selectedDispo);

      // this.resetAll();

      this.searchSelected = -1;
      this.search = "";

      this.disposSelected = selectedDispo;
      console.log("dispo in Parent: ", this.disposSelected);

      for (var i in this.stages) {
        var stage = this.stages[i];
        this.$refs[`stage${stage.id}`][0].filterByDispo(this.disposSelected);
      }
    },
  },
};
</script>

<style scoped>
/* Stages Container */
.stages-container {
  background: var(--v-background-base, #f5f5f5);
  min-height: 100vh;
  padding: 0;
}

.theme--dark .stages-container {
  background: #121212 !important;
}

/* Modern Card */
.modern-card {
  background: var(--v-background-base, #ffffff);
  border-radius: 12px !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden !important;
  transition: all 0.3s;
}

.theme--dark .modern-card {
  background: #1e1e1e !important;
}

.modern-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.card-header {
  background: linear-gradient(135deg, #37cf37 0%, #2ba82b 100%);
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 12px 12px 0 0;
}

.icon-badge {
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
}

.card-title {
  color: #ffffff;
  font-size: 18px;
  font-weight: 600;
}

.card-subtitle {
  color: rgba(255, 255, 255, 0.85);
  font-size: 13px;
  font-weight: 400;
}

/* Stages Wrapper */
.stages-wrapper {
  background: var(--v-background-base, #ffffff) !important;
}

.theme--dark .stages-wrapper {
  background: #1e1e1e !important;
}

.stages-scroll-container {
  display: flex;
  gap: 16px;
  overflow-x: auto;
  padding-bottom: 8px;
}

.stages-scroll-container::-webkit-scrollbar {
  height: 8px;
}

.stages-scroll-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.theme--dark .stages-scroll-container::-webkit-scrollbar-track {
  background: #2d2d2d;
}

.stages-scroll-container::-webkit-scrollbar-thumb {
  background: #37cf37;
  border-radius: 4px;
}

.stages-scroll-container::-webkit-scrollbar-thumb:hover {
  background: #2ba82b;
}

/* Stage Column */
.stage-column {
  flex-shrink: 0;
}

/* Minimized Stage */
.stage-minimized {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border: 1px solid #dee2e6;
  border-radius: 12px;
  height: 500px;
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.theme--dark .stage-minimized {
  background: linear-gradient(135deg, #2d2d2d 0%, #252525 100%) !important;
  border-color: #404040 !important;
}

.stage-minimized-content {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  writing-mode: vertical-rl;
  text-orientation: mixed;
}

.stage-minimized-title {
  color: #495057;
  font-size: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.theme--dark .stage-minimized-title {
  color: #e0e0e0 !important;
}

/* Stage Card */
.stage-card {
  background: var(--v-background-base, #ffffff) !important;
  border: 1px solid #e0e0e0 !important;
  border-radius: 12px !important;
  overflow: hidden;
}

.theme--dark .stage-card {
  background: #1e1e1e !important;
  border-color: #333333 !important;
}

.stage-header {
  background: linear-gradient(to right, #f8f9fa, #ffffff);
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  border-bottom: 1px solid #e0e0e0;
}

.stage-name-input {
  flex: 1;
  font-weight: 600 !important;
  font-size: 14px !important;
}

.stage-count-badge {
  flex-shrink: 0;
}

.stage-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.stage-search {
  padding: 12px 16px;
  background: #f8f9fa;
}

.search-input {
  border-radius: 8px;
}

.stage-content {
  height: 74vh;
  overflow-y: auto;
  padding: 12px;
}

.stage-content-search {
  height: 68vh;
}

.stage-content::-webkit-scrollbar {
  width: 6px;
}

.stage-content::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.stage-content::-webkit-scrollbar-thumb {
  background: #37cf37;
  border-radius: 3px;
}

.stage-content::-webkit-scrollbar-thumb:hover {
  background: #2ba82b;
}

/* Add Stage Column */
.add-stage-column {
  flex-shrink: 0;
  width: 272px;
  min-width: 272px;
}

.add-stage-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%) !important;
  border: 2px dashed #37cf37 !important;
  border-radius: 12px !important;
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  transition: all 0.3s;
}

.add-stage-card:hover {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%) !important;
  border-color: #2ba82b !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(55, 207, 55, 0.15);
}

/* Legacy styles for compatibility */
.scroll {
  overflow-x: scroll;
}

.fs {
  font-size: 14px;
}

.rot-90 {
  transform: rotate(90deg);
}

.rel {
  position: relative;
  padding-bottom: 2.5rem;
}

.abs {
  position: absolute;
  bottom: 0px;
}
</style>

<style>
/* Global scrollbar styles */
::-webkit-scrollbar {
  width: 10px;
}

::-webkit-scrollbar-thumb {
  background: rgba(55, 207, 55, 0.3);
  border-radius: 5px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(55, 207, 55, 0.5);
}
</style>
